// MockVenueAdapter — simulates the Forwardsflow venue connection
// Pattern follows src/services/kycVendors/screeningAdapter.js

import {
  mockRfqs, mockQuotes, mockCounterparties, mockCompetitiveContext, mockAuditLog,
} from '../../data/lpMockData';

let _quoteIdCounter = 8000;
const generateQuoteId = () => `QT-${_quoteIdCounter++}`;

export class MockVenueAdapter {
  constructor() {
    this.name = 'mock-venue';
    this._rfqs = new Map(mockRfqs.map(r => [r.id, { ...r }]));
    this._quotes = new Map(mockQuotes.map(q => [q.id, { ...q }]));
    this._counterparties = new Map(mockCounterparties.map(c => [c.id, { ...c }]));
    this._auditLog = [...mockAuditLog];
    this._listeners = { rfq: [], counterpartyStatus: [] };
    this._intervals = [];
  }

  isAvailable() {
    return true;
  }

  // ─── RFQ Stream ─────────────────────────────────────────────────────
  /**
   * Subscribe to incoming RFQs. Calls `onRfq(rfq)` for existing and new RFQs.
   * Returns an unsubscribe function.
   */
  streamRfqs(onRfq) {
    // Emit existing RFQs
    for (const rfq of this._rfqs.values()) {
      onRfq({ ...rfq });
    }

    // Simulate new RFQs arriving periodically
    const interval = setInterval(() => {
      // Randomly push a status update on an existing live RFQ (simulate deadline approach)
      const liveRfqs = [...this._rfqs.values()].filter(r => r.status === 'live');
      if (liveRfqs.length > 0) {
        const rfq = liveRfqs[Math.floor(Math.random() * liveRfqs.length)];
        onRfq({ ...rfq });
      }
    }, 5000 + Math.random() * 3000);

    this._intervals.push(interval);

    return () => {
      clearInterval(interval);
      this._intervals = this._intervals.filter(i => i !== interval);
    };
  }

  // ─── RFQ Access ─────────────────────────────────────────────────────
  async getRfq(rfqId) {
    await delay(100);
    const rfq = this._rfqs.get(rfqId);
    return rfq ? { ...rfq } : null;
  }

  // ─── Quote Actions ──────────────────────────────────────────────────
  async submitQuote({ rfqId, currencyPair, side, amount, midRate, margin, clientRate, counterAmount, dealerName, offPolicy, seniorApproval }) {
    await delay(200);
    const id = generateQuoteId();
    const quote = {
      id,
      rfqId,
      counterpartyId: this._rfqs.get(rfqId)?.counterpartyId,
      currencyPair,
      side,
      amount,
      midRate,
      margin,
      clientRate,
      counterAmount,
      status: 'live',
      submittedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 300000).toISOString(), // 5 min
      dealerName,
      seniorApproval: seniorApproval || null,
      offPolicy: offPolicy || false,
    };
    this._quotes.set(id, quote);

    // Update RFQ status
    const rfq = this._rfqs.get(rfqId);
    if (rfq && rfq.status === 'live') {
      rfq.status = 'quoted';
      this._rfqs.set(rfqId, rfq);
    }

    this._logAudit('quote_submitted', dealerName, 'quote', id,
      `Submitted rate ${clientRate} for ${rfqId} (${currencyPair} ${formatAmount(amount)})`);

    return { ...quote };
  }

  async amendQuote(quoteId, { margin, clientRate, counterAmount, dealerName }) {
    await delay(150);
    const quote = this._quotes.get(quoteId);
    if (!quote) throw new Error(`Quote ${quoteId} not found`);
    if (quote.status !== 'live' && quote.status !== 'amended') {
      throw new Error(`Cannot amend quote in status ${quote.status}`);
    }

    quote.margin = margin;
    quote.clientRate = clientRate;
    quote.counterAmount = counterAmount;
    quote.status = 'amended';
    this._quotes.set(quoteId, quote);

    this._logAudit('quote_amended', dealerName, 'quote', quoteId,
      `Amended to rate ${clientRate}`);

    return { ...quote };
  }

  async withdrawQuote(quoteId, dealerName) {
    await delay(150);
    const quote = this._quotes.get(quoteId);
    if (!quote) throw new Error(`Quote ${quoteId} not found`);
    if (quote.status !== 'live' && quote.status !== 'amended') {
      throw new Error(`Cannot withdraw quote in status ${quote.status}`);
    }

    quote.status = 'withdrawn';
    this._quotes.set(quoteId, quote);

    this._logAudit('quote_withdrawn', dealerName, 'quote', quoteId,
      `Withdrawn quote for ${quote.currencyPair} ${formatAmount(quote.amount)}`);

    return { ...quote };
  }

  async declineRfq(rfqId, reason, dealerName) {
    await delay(150);
    const rfq = this._rfqs.get(rfqId);
    if (!rfq) throw new Error(`RFQ ${rfqId} not found`);

    rfq.status = 'declined';
    this._rfqs.set(rfqId, rfq);

    this._logAudit('rfq_declined', dealerName, 'rfq', rfqId,
      `Declined RFQ: ${reason}`);

    return { ...rfq };
  }

  // ─── Counterparty ───────────────────────────────────────────────────
  async getCounterparty(counterpartyId) {
    await delay(100);
    const cp = this._counterparties.get(counterpartyId);
    return cp ? { ...cp } : null;
  }

  // ─── Evidence Pack ──────────────────────────────────────────────────
  async getEvidencePack(rfqId, dealerName) {
    await delay(200);
    const rfq = this._rfqs.get(rfqId);
    const cp = rfq ? this._counterparties.get(rfq.counterpartyId) : null;
    const quotes = [...this._quotes.values()].filter(q => q.rfqId === rfqId);

    this._logAudit('evidence_pack_accessed', dealerName, 'compliance', rfqId,
      `Accessed evidence pack for ${rfqId}`);

    return {
      rfq: rfq ? { ...rfq } : null,
      counterparty: cp ? { ...cp } : null,
      quotes: quotes.map(q => ({ ...q })),
      accessedAt: new Date().toISOString(),
      accessedBy: dealerName,
    };
  }

  // ─── Competitive Context ────────────────────────────────────────────
  async getCompetitiveContext(amount) {
    await delay(100);
    if (amount <= 500000) return { ...mockCompetitiveContext.small };
    if (amount <= 2000000) return { ...mockCompetitiveContext.medium };
    return { ...mockCompetitiveContext.large };
  }

  // ─── Trade Confirmation ─────────────────────────────────────────────
  async confirmTrade(tradeId) {
    await delay(200);
    return { tradeId, confirmed: true, confirmedAt: new Date().toISOString() };
  }

  // ─── Settlement ─────────────────────────────────────────────────────
  async updateSettlement(tradeId, update) {
    await delay(150);
    return { tradeId, ...update, updatedAt: new Date().toISOString() };
  }

  // ─── Counterparty Status Stream ─────────────────────────────────────
  /**
   * Simulates a counterparty status change (cp-006 goes suspended after 60s).
   */
  streamCounterpartyStatus(onStatusChange) {
    const timeout = setTimeout(() => {
      const cp = this._counterparties.get('cp-006');
      if (cp && cp.kycStatus !== 'suspended') {
        cp.kycStatus = 'suspended';
        this._counterparties.set('cp-006', cp);
        onStatusChange({ counterpartyId: 'cp-006', newStatus: 'suspended', previousStatus: 'verified' });

        this._logAudit('counterparty_suspended', 'System', 'counterparty', 'cp-006',
          'Greenfield Capital KYC status changed: verified -> suspended');
      }
    }, 60000);

    this._intervals.push(timeout);

    return () => {
      clearTimeout(timeout);
      this._intervals = this._intervals.filter(i => i !== timeout);
    };
  }

  // ─── Quotes & Audit Access ──────────────────────────────────────────
  getQuotes() {
    return [...this._quotes.values()].map(q => ({ ...q }));
  }

  getQuotesByRfq(rfqId) {
    return [...this._quotes.values()].filter(q => q.rfqId === rfqId).map(q => ({ ...q }));
  }

  getAuditLog() {
    return [...this._auditLog].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  // ─── Cleanup ────────────────────────────────────────────────────────
  destroy() {
    this._intervals.forEach(i => {
      clearInterval(i);
      clearTimeout(i);
    });
    this._intervals = [];
  }

  // ─── Internal ───────────────────────────────────────────────────────
  _logAudit(action, user, entityType, entityId, details) {
    this._auditLog.unshift({
      id: `AUD-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action,
      user,
      entityType,
      entityId,
      details,
      ipAddress: user === 'System' ? null : '10.0.1.45',
    });
  }
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function formatAmount(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}
